import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import SectionHeading from "../SectionHeading";
import Icon from "../Icon";
import { INDUSTRIES, IMAGES } from "../../data/content";

export default function IndustriesFeature() {
  const [active, setActive] = useState(0);
  const ind = INDUSTRIES[active];

  return (
    <section className="ad-section bg-white" data-testid="industries-feature">
      <div className="ad-container">
        <SectionHeading
          eyebrow="Industries"
          title={<>Intelligence built for the way <span className="text-gradient">your industry works.</span></>}
        />

        <div className="mt-12 grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-12">
          {/* Selector */}
          <div className="flex flex-col gap-1.5">
            {INDUSTRIES.map((item, i) => (
              <button
                key={item.title}
                onClick={() => setActive(i)}
                data-testid={`industry-tab-${i}`}
                aria-pressed={active === i}
                className={`group flex items-center justify-between rounded-2xl border px-5 py-4 text-left transition-all duration-300 ${
                  active === i
                    ? "border-[#1677FF]/40 bg-[#1677FF]/5"
                    : "border-[#111827]/8 bg-white hover:border-[#111827]/20"
                }`}
              >
                <span className="flex items-center gap-3">
                  <span className={`flex h-10 w-10 items-center justify-center rounded-xl transition-colors ${active === i ? "bg-[#1677FF] text-white" : "bg-[#F3F5F7] text-[#1677FF]"}`}>
                    <Icon name={item.icon} className="h-5 w-5" />
                  </span>
                  <span className={`text-base font-bold ${active === i ? "text-[#111827]" : "text-[#374151]"}`}>{item.title}</span>
                </span>
                <ArrowRight className={`h-4 w-4 transition-all ${active === i ? "text-[#1677FF]" : "text-[#9CA3AF] group-hover:translate-x-1"}`} />
              </button>
            ))}
          </div>

          {/* Detail panel */}
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden rounded-3xl border border-[#111827]/8 bg-white shadow-[0_30px_60px_-40px_rgba(17,24,39,0.35)]"
              data-testid="industry-detail"
            >
              <div className="img-overlay-blue relative">
                <img src={IMAGES[ind.image]} alt={`${ind.title} industry`} className="h-52 w-full object-cover lg:h-60" loading="lazy" />
                <h3 className="absolute bottom-5 left-6 text-2xl font-extrabold text-white">{ind.title}</h3>
              </div>
              <div className="p-7 lg:p-8">
                <p className="text-sm font-semibold text-[#1677FF]">{ind.tagline}</p>
                <div className="mt-5 space-y-4 text-sm">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Challenge</p>
                    <p className="mt-1 text-[#374151]">{ind.challenge}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Our solution</p>
                    <p className="mt-1 text-[#374151]">{ind.solution}</p>
                  </div>
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  {ind.useCases.map((u) => (
                    <span key={u} className="inline-flex items-center gap-1.5 rounded-full border border-[#111827]/10 bg-[#F7F8FA] px-3 py-1.5 text-xs text-[#374151]">
                      <Check className="h-3 w-3 text-[#1677FF]" /> {u}
                    </span>
                  ))}
                </div>
                <Link to="/contact" className="group mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1677FF] hover:text-[#00C7D9]">
                  Discuss your use case <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
